/**
 * rum.ts — RUM старта мини-апа. Публичный fire-and-forget beacon → mobile-gw
 * `POST /v1/miniapp/rum` (без auth: сигнал летит ДО авторизации). Ловим боль
 * «совсем не открылось / долго открывалось». Смотреть: `padla look miniapp`.
 * План: docs/superpowers/plans/2026-07-01-miniapp-connectivity-observability.md
 *
 * boot_started шлётся inline-скриптом в index.html ДО бандла (index долетел, JS
 * пошёл). app_boot — отсюда, когда приложение реально пригодно (первый осевший
 * запрос данных): ловит и «шелл есть, но /api завис» (firstApiOk=false).
 */

export interface AppBootPayload {
  bundleMs?: number;
  firstApiMs?: number;
  firstApiOk?: boolean;
  retries?: number;
}

type FetchLike = typeof fetch;

/** Низкоуровневый посыл — никогда не бросает, никогда не блокирует UX (П5). */
export function sendRum(
  body: Record<string, unknown>,
  apiBase = '/api',
  fetchImpl: FetchLike = fetch,
  onError?: (message: string) => void,
): void {
  try {
    void fetchImpl(`${apiBase}/v1/miniapp/rum`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch((e: unknown) => onError?.(String(e)));
  } catch (e) {
    /* сбор метрик не критичен */
    onError?.(String(e));
  }
}

/** app_boot — полный старт (или частичный сбой первого /api). Один раз на загрузку. */
export function trackAppBoot(
  payload: AppBootPayload,
  apiBase = '/api',
  fetchImpl: FetchLike = fetch,
): void {
  bootDiag.attempted = true;
  sendRum({ type: 'app_boot', ...payload }, apiBase, fetchImpl, (message) => {
    bootDiag.sendError = message;
  });
}

/**
 * Донесение «страница ушла, а маяка приложения не было» (умный вход 5.1, хвост «маяк приложения», 28 сен 2026). У
 * части заходов на iPhone и Android приложение работало (вход, состояние, рефералы — 200), а маяк `app_boot` не
 * приходил; с сервера причины не видно, а сбой плавающий — один телефон в руках его не поймает. Поэтому страница
 * сама сообщает, уходя: сколько жила, сколько запросов данных осело успехом и ошибкой, пыталась ли отправить маяк и
 * с какой ошибкой, каким мостом шла. Уходит `sendBeacon` — доходит при закрытии, идёт мимо гонки мостов (это не
 * fetch) и без предзапроса (text/plain). Личного — ничего: числа и короткие метки.
 */
export interface BootDiag {
  /** performance.now() на старте приложения. */
  t0: number;
  /** Запросов данных осело успехом и ошибкой (события кэша react-query). */
  settledOk: number;
  settledErr: number;
  /** Маяк app_boot отправлялся; ошибка его отправки (null — не было или ещё летит). */
  attempted: boolean;
  sendError: string | null;
}

export const bootDiag: BootDiag = { t0: 0, settledOk: 0, settledErr: 0, attempted: false, sendError: null };

export interface EdgeInfo {
  /** Запросы идут на адрес самой страницы (own) или гонка мостов переехала на запасной (other). */
  own: boolean;
  /** Какой мост отдал страницу (заголовок X-Padla-Bridge); null — не назвался. */
  served: string | null;
}

export function bootDiagPayload(
  d: BootDiag,
  now: number,
  edge: EdgeInfo,
  how: 'hidden' | 'pagehide',
): Record<string, unknown> {
  return {
    type: 'app_boot_missing',
    reason: d.attempted ? 'send_failed' : 'never_attempted',
    how,
    aliveMs: Math.max(0, Math.round(now - d.t0)),
    ok: d.settledOk,
    err: d.settledErr,
    ...(d.sendError ? { sendError: d.sendError.slice(0, 120) } : {}),
    edge: edge.own ? 'own' : 'other',
    served: edge.served,
  };
}

/** Нужно ли донесение: маяк не отправлялся или его отправка упала. Летящий маяк — не повод. */
export function bootDiagDue(d: BootDiag): boolean {
  return !d.attempted || d.sendError !== null;
}

export interface BootDiagDeps {
  edge: () => EdgeInfo;
  apiBase?: string;
  now?: () => number;
  beacon?: (url: string, body: string) => boolean;
  target?: { addEventListener(type: string, fn: () => void): void };
  doc?: { addEventListener(type: string, fn: () => void): void; visibilityState: string };
}

/** Донесение — один раз на загрузку, в момент ухода страницы (скрытие или выгрузка — что раньше). */
export function installBootDiagnostics(deps: BootDiagDeps): void {
  let done = false;
  const fire = (how: 'hidden' | 'pagehide'): void => {
    if (done || !bootDiagDue(bootDiag)) return;
    done = true;
    try {
      const body = JSON.stringify(bootDiagPayload(bootDiag, (deps.now ?? (() => performance.now()))(), deps.edge(), how));
      (deps.beacon ?? ((u: string, b: string) => navigator.sendBeacon(u, b)))(`${deps.apiBase ?? '/api'}/v1/miniapp/rum`, body);
    } catch {
      /* диагностика никогда не ломает страницу */
    }
  };
  const doc = deps.doc ?? document;
  (deps.target ?? window).addEventListener('pagehide', () => fire('pagehide'));
  doc.addEventListener('visibilitychange', () => {
    if (doc.visibilityState === 'hidden') fire('hidden');
  });
}

export interface ClientErrorPayload {
  message: string;
  stack?: string;
  componentStack?: string;
  url?: string;
}

const cut = (v: string | null | undefined, n: number): string | undefined =>
  typeof v === 'string' && v.length > 0 ? v.slice(0, n) : undefined;

/**
 * Краш-репорт UI → mobile-gw `POST /v1/miniapp/client-error` (без auth: краш
 * бывает и до логина). Консоль Telegram-webview невидима — это единственный
 * канал, по которому рендер-краш мини-апа доезжает до логов прода
 * (evt=miniapp_client_error). Никогда не бросает (П5); keepalive — доходит,
 * даже если вкладка умирает.
 */
export function reportClientError(
  payload: ClientErrorPayload,
  apiBase = '/api',
  fetchImpl: FetchLike = fetch,
): void {
  try {
    void fetchImpl(`${apiBase}/v1/miniapp/client-error`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: cut(payload.message, 500) ?? 'unknown',
        stack: cut(payload.stack, 8000),
        componentStack: cut(payload.componentStack, 8000),
        url: cut(payload.url ?? window.location?.href, 300),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* репорт краша не должен добивать UX */
  }
}
